import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { SIGNUP_INTENT_FLAG_KEY } from "../auth/useOnboardingState";

// Signup page: same two OAuth buttons as Login but gated on an
// explicit TOS checkbox + carries `?mode=signup` so the orchestrator
// runs the lookup-or-create flow. Login carries `?mode=signin` and
// gets a "no account, sign up?" redirect on miss (0024).
//
// The localStorage `pmt-signup-intent` flag is set in onClick BEFORE
// browser navigation so onboarding can detect "this user came in via
// the signup path" and fire the welcome flow even when the
// `created_at < 5min` check would otherwise miss (cross-device case;
// see useOnboardingState + 0024 F3 / F10).
const API_BASE = import.meta.env.VITE_API_BASE_URL ?? window.location.origin;

export function Signup() {
  const [params] = useSearchParams();
  const [tosAccepted, setTosAccepted] = useState(false);

  // `?from=signin-no-account` lands here when the Login page nudges
  // a user who tried to sign in but doesn't have an account yet.
  // Login renders the orchestrator's `signin_no_account=1` flag as
  // a banner with a Sign-up link; that link forwards them here. We
  // acknowledge the redirect so the user knows why they ended up
  // on Sign up.
  const fromSigninNoAccount = params.get("from") === "signin-no-account";
  // `?from=tos-required` lands here when the user attempted signup
  // without `?tos_accepted=1` (0025) — orchestrator rejected at
  // user-creation time and bounced back. Banner asks them to check
  // the box.
  const fromTosRequired = params.get("from") === "tos-required";

  const returnTo = `${window.location.origin}/`;

  // 0025: only include `&tos_accepted=1` when the box is actually
  // checked. handleOAuthClick already blocks navigation when
  // unchecked, so this is belt-and-suspenders — but keeping the
  // wire fingerprint honest matters for the audit log + future
  // automated verification.
  const buildOAuthUrl = (provider: "google" | "microsoft") => {
    const tosParam = tosAccepted ? "&tos_accepted=1" : "";
    return `${API_BASE}/api/v1/auth/oauth/${provider}/start?mode=signup${tosParam}&return_to=${encodeURIComponent(returnTo)}`;
  };

  // Click guard for the OAuth links: blocks navigation when TOS
  // isn't checked, and stamps the signup-intent flag right before
  // navigation when it is. The link's pointer-events styling already
  // gives a visual "disabled" cue; this is the functional gate.
  const handleOAuthClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!tosAccepted) {
      e.preventDefault();
      return;
    }
    try {
      window.localStorage.setItem(SIGNUP_INTENT_FLAG_KEY, "1");
    } catch {
      // Private mode / quota — fail silently. Onboarding gate's
      // isFreshAccount backstop still fires for fresh signups.
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center px-6">
      <div
        className="pointer-events-none absolute -top-40 left-1/2 h-[500px] w-[700px] -translate-x-1/2 rounded-full opacity-20 blur-3xl"
        style={{
          background: "radial-gradient(circle, rgba(124,122,255,0.6), transparent 70%)",
        }}
      />

      <div className="relative w-full max-w-sm animate-slide-up">
        {fromSigninNoAccount ? (
          <div
            className="mb-6 rounded-md border border-warn-solid/40 bg-warn-solid/10 px-3 py-2 text-xs text-warn-strong"
            role="status"
          >
            We didn't find an account for that sign-in. Review and accept
            the terms below to create a new account.
          </div>
        ) : null}

        {fromTosRequired ? (
          // 0025: orchestrator rejected the signup at user-creation
          // time because tos_accepted wasn't set. The user was
          // bounced back here to re-do with the checkbox.
          <div
            className="mb-6 rounded-md border border-warn-solid/40 bg-warn-solid/10 px-3 py-2 text-xs text-warn-strong"
            role="status"
          >
            Please accept the terms below before continuing.
          </div>
        ) : null}

        <div className="mb-12">
          <div className="flex items-center gap-2 mb-1">
            <div className="h-1.5 w-1.5 rounded-full bg-accent" />
            <span className="font-mono text-xs text-ink-300 tracking-widest uppercase">
              Eminity
            </span>
          </div>
          <h1 className="text-4xl font-medium text-ink-50 tracking-tightest mt-6">Sign up</h1>
          <p className="mt-3 text-sm text-ink-300 leading-relaxed">
            Eminity turns a one-line idea into a build-ready spec
            through iterative LLM analysis. Create an account to start.
          </p>
        </div>

        <label className="mb-6 flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={tosAccepted}
            onChange={(e) => setTosAccepted(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 rounded border border-ink-600 bg-ink-900 accent-accent focus:outline-none focus:ring-1 focus:ring-accent cursor-pointer"
          />
          <span className="text-xs text-ink-300 leading-relaxed">
            I agree to the{" "}
            {/* target=_blank so the user keeps their place in the
                signup flow + their checkbox state (0024 F9). */}
            <a
              href="/legal/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-ink-200 hover:text-accent underline-offset-2 hover:underline"
            >
              terms of service
            </a>{" "}
            and{" "}
            <a
              href="/legal/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-ink-200 hover:text-accent underline-offset-2 hover:underline"
            >
              privacy policy
            </a>
            .
          </span>
        </label>

        <div className="space-y-2">
          <a
            href={buildOAuthUrl("google")}
            onClick={handleOAuthClick}
            aria-disabled={!tosAccepted}
            className={`group w-full flex items-center justify-between px-4 py-3 bg-ink-900 border rounded-md text-sm transition-colors ${
              tosAccepted
                ? "border-ink-700 hover:border-ink-500 text-ink-100"
                : "border-ink-800 text-ink-500 cursor-not-allowed"
            }`}
          >
            <span className="flex items-center gap-3">
              <GoogleMark dimmed={!tosAccepted} />
              Continue with Google
            </span>
            <span className={tosAccepted ? "text-ink-400 group-hover:text-ink-200 transition-colors" : "text-ink-600"}>→</span>
          </a>

          <a
            href={buildOAuthUrl("microsoft")}
            onClick={handleOAuthClick}
            aria-disabled={!tosAccepted}
            className={`group w-full flex items-center justify-between px-4 py-3 bg-ink-900 border rounded-md text-sm transition-colors ${
              tosAccepted
                ? "border-ink-700 hover:border-ink-500 text-ink-100"
                : "border-ink-800 text-ink-500 cursor-not-allowed"
            }`}
          >
            <span className="flex items-center gap-3">
              <MicrosoftMark dimmed={!tosAccepted} />
              Continue with Microsoft
            </span>
            <span className={tosAccepted ? "text-ink-400 group-hover:text-ink-200 transition-colors" : "text-ink-600"}>→</span>
          </a>
        </div>

        <p className="mt-10 text-xs text-ink-400 leading-relaxed">
          Already have an account?{" "}
          <Link
            to="/login"
            className="text-ink-200 hover:text-accent underline-offset-2 hover:underline"
          >
            Sign in
          </Link>
          .
        </p>

        <div className="mt-16 pt-6 border-t border-ink-800 flex items-center justify-between text-xs font-mono text-ink-400">
          <span>{import.meta.env.VITE_API_BASE_URL ?? "(dev)"}</span>
        </div>
      </div>
    </div>
  );
}

function GoogleMark({ dimmed }: { dimmed?: boolean }) {
  const opacity = dimmed ? 0.4 : 1;
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" style={{ opacity }}>
      <path fill="#4285F4" d="M22.5 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.32z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.1A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.44.34-2.1V7.06H2.18A11 11 0 0 0 1 12c0 1.78.43 3.46 1.18 4.94l3.66-2.84z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.1 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
    </svg>
  );
}

function MicrosoftMark({ dimmed }: { dimmed?: boolean }) {
  const opacity = dimmed ? 0.4 : 1;
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" style={{ opacity }}>
      <path fill="#F25022" d="M1 1h10v10H1z"/>
      <path fill="#7FBA00" d="M13 1h10v10H13z"/>
      <path fill="#00A4EF" d="M1 13h10v10H1z"/>
      <path fill="#FFB900" d="M13 13h10v10H13z"/>
    </svg>
  );
}
